package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.FunDecl
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.SubscribeBlockAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * Stage B：subscribe 订阅块语义校验。
 *
 * 检查内容：
 * - E780：多 subscribe 顶层块重复声明
 * - E781：subscribe 命名空间名非法（应符合 Java 标识符规则）
 * - E782：subscribe 主题名非法（应符合 Java 标识符规则）
 * - E783：(namespace, topic) 重复声明 — 含顶层块互相重复，以及顶层块字面量参数与
 *   `net.subscribe(...)` 字面量参数重复
 * - E784：handler 必须是 ::funRef 或 1 参 lambda
 * - E785：subscribe 条目未知字段（仅支持 handler）
 *
 * 实现备注：表达式 / 语句的全树遍历由 [ExprVisitor] 通用基类提供（默认全递归子节点），
 * 本类只在内部 [NetSubscribeLiteralVisitor] 中 override [ExprVisitor.visitBuiltinCall]
 * 命中 `net.subscribe` 字面量参数后做去重判定。这避免了与其它 Checker（CloseBlockChecker
 * 等）各自维护一份 30+ 分支 walker 的样板代码重复。
 */
class SubscribeChecker(private val diag: DiagnosticCollector) {

    companion object {
        /** 命名合法性正则：Java 标识符规则。 */
        private val IDENTIFIER_PATTERN = Regex("^[A-Za-z_][A-Za-z0-9_]*$")

        /** lambda handler 的形参名约定：与 net.request 回调同样使用 `event`。 */
        const val HANDLER_LAMBDA_PARAM_NAME = "event"
    }

    fun check(screen: ScreenAst) {
        // E780：多 subscribe 顶层块（保留首块、其余报错）
        if (screen.subscribeBlocks.size > 1) {
            screen.subscribeBlocks.drop(1).forEach { block ->
                diag.error(DslError.E780_SUBSCRIBE_BLOCK_DUPLICATE, block.position)
            }
        }

        val seen = mutableSetOf<Pair<String, String>>()
        for (block in screen.subscribeBlocks) {
            for (item in block.items) {
                checkItem(item, seen)
            }
            // E785：parser 阶段保留下来的未知字段
            for (uf in block.unknownFields) {
                diag.error(DslError.E785_SUBSCRIBE_FIELD_UNKNOWN, uf.position, uf.fieldName)
            }
        }

        // E783 补充：扫描整个 screen（root + actions + funs + subscribe handlers）中
        // BuiltinCallExpr name == "net.subscribe" 的字面量 (ns, topic)，与顶层块去重。
        scanNetSubscribeLiterals(screen, seen)
    }

    private fun checkItem(item: SubscribeBlockAst.Item, seen: MutableSet<Pair<String, String>>) {
        // E781：命名空间合法性
        if (!IDENTIFIER_PATTERN.matches(item.namespace)) {
            diag.error(DslError.E781_SUBSCRIBE_NAMESPACE_INVALID, item.position, item.namespace)
        }
        // E782：主题名合法性
        if (!IDENTIFIER_PATTERN.matches(item.topic)) {
            diag.error(DslError.E782_SUBSCRIBE_TOPIC_INVALID, item.position, item.topic)
        }
        // E783：(namespace, topic) 去重
        val key = item.namespace to item.topic
        if (!seen.add(key)) {
            diag.error(DslError.E783_SUBSCRIBE_TOPIC_DUPLICATE, item.position, item.namespace, item.topic)
        }
        // E784：handler 形态
        checkHandler(item.handler)
    }

    private fun checkHandler(handler: ExprAst) {
        if (handler is ExprAst.FunRefExpr) return
        if (handler is ExprAst.LambdaExpr && handler.params.size == 1) return
        diag.error(DslError.E784_SUBSCRIBE_HANDLER_INVALID, handler.position, "")
    }

    /**
     * 扫描整个 ScreenAst 中所有 `net.subscribe(ns, topic, handler)` 形态的调用，
     * 当 ns / topic 都是字符串字面量时按 (ns, topic) 入 [seen] 去重；与顶层块条目重复
     * 即报 E783，与 net.subscribe 字面量参数互相重复也报 E783。
     */
    private fun scanNetSubscribeLiterals(screen: ScreenAst, seen: MutableSet<Pair<String, String>>) {
        val visitor = NetSubscribeLiteralVisitor()
        // 元素树
        scanElement(visitor, screen.root, seen)
        // funs（含其它生命周期 / 嵌套）
        scanFuns(visitor, screen.funs, seen)
        // open / close / show / hide 体
        screen.openBody?.forEach { visitor.visitStmt(it, seen) }
        screen.closeBody?.forEach { visitor.visitStmt(it, seen) }
        screen.showBody?.forEach { visitor.visitStmt(it, seen) }
        screen.hideBody?.forEach { visitor.visitStmt(it, seen) }
        // onKey / onKeyRelease / onMouseDown / onMouseUp / onMouseMove lambda 体
        screen.onKeyLambda?.body?.forEach { visitor.visitStmt(it, seen) }
        screen.onKeyReleaseLambda?.body?.forEach { visitor.visitStmt(it, seen) }
        screen.onMouseDownLambda?.body?.forEach { visitor.visitStmt(it, seen) }
        screen.onMouseUpLambda?.body?.forEach { visitor.visitStmt(it, seen) }
        screen.onMouseMoveLambda?.body?.forEach { visitor.visitStmt(it, seen) }
        // 顶层 subscribe 块自身的 handler 也要扫（lambda body 内可能再调 net.subscribe）
        for (block in screen.subscribeBlocks) {
            for (item in block.items) {
                if (item.handler is ExprAst.LambdaExpr) {
                    item.handler.body.forEach { visitor.visitStmt(it, seen) }
                }
            }
        }
    }

    private fun scanFuns(
        visitor: NetSubscribeLiteralVisitor,
        funs: List<FunDecl>,
        seen: MutableSet<Pair<String, String>>,
    ) {
        for (f in funs) f.body.forEach { visitor.visitStmt(it, seen) }
    }

    private fun scanElement(
        visitor: NetSubscribeLiteralVisitor,
        element: ElementAst,
        seen: MutableSet<Pair<String, String>>,
    ) {
        for (prop in element.props) visitor.visit(prop.value, seen)
        for (modifier in element.modifiers) {
            for (field in modifier.fields) visitor.visit(field.value, seen)
        }
        for (child in element.children) scanElement(visitor, child, seen)
    }

    /**
     * 内部 [ExprVisitor] 子类：仅 override [visitNetSubscribe] 命中字面量去重判定，
     * 其余节点继续走基类的默认全递归。
     *
     * 上下文 ctx 即累积重复检测用的 `Set<Pair<String, String>>`。
     */
    private inner class NetSubscribeLiteralVisitor : ExprVisitor<MutableSet<Pair<String, String>>>() {
        override fun visitNetSubscribe(
            e: ExprAst.NetSubscribeCall,
            ctx: MutableSet<Pair<String, String>>,
        ) {
            // namespace / topic 在 parser 阶段已从 "ns.topic" 字面量拆出，直接读字段去重。
            val key = e.namespace to e.topic
            if (!ctx.add(key)) {
                diag.error(
                    DslError.E783_SUBSCRIBE_TOPIC_DUPLICATE,
                    e.position,
                    e.namespace,
                    e.topic,
                )
            }
            // 继续递归 handler 子节点（lambda body 内可能再嵌 net.subscribe）
            super.visitNetSubscribe(e, ctx)
        }
    }
}
